import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ModalVariables, Table, TableSkeleton, TopProgress } from "../../components";
import { useAuth } from "../../context/user.context";
import { icons } from "../../icons";
import { InstanceName } from "../Account-settings/styles";
import {Container} from './styles'
import { timeConverterMMDDYYHHMM } from "../../utils/dates";
import { useNavigate } from "react-router-dom";
import { authService, clientService, permissionService } from "../../api";
import { useUi } from "../../context/ui.context";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { permissionSurfaces, usePermission } from '../../permissions';

const Clients:FC = () => {
  const {user, setUser, updateUser, setClientToInvite, setAvailableRoles, setAvailableNotifications} = useAuth()
  const [rows, setRows] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam)
  const [search] = useQueryParam('search', StringParam)

  const [paginationData, setPaginationData] = useState<any>()
  const [t] = useTranslation()
  const { mergeEntityPermissions } = usePermission()

  const updateList = (silent?:boolean) => {
    if(!silent) setPageLoading(true)
    clientService.getClientList({
      page:page||1,
      ordering,
      pageSize:pageSize||10,
      search
    })
    .then((res) => {
      mergeEntityPermissions('clients', res.data, 'Merged clients list permissions')
      let data = res.data.objects.map((client:any) => {
        const roleItems = Array.isArray(client.roles) ? client.roles : []
        const roleNames = roleItems.map((role:any) => role.display_name || role.name || '')
        const isOwner = roleItems.some((role:any) => {
          if (String(role?.id) === '1') {
            return true
          }

          const normalizedRoleName = String(role?.display_name || role?.name || '').toLowerCase()

          return normalizedRoleName.includes('owner')
        })
        const ownerFallbackAllowedActions = isOwner
          ? ['send_invitation', 'invite', 'create', 'update', 'edit', 'dissociate_from_client', 'dissociate', 'delete', 'remove']
          : []
        const allowedActions = Array.from(
          new Set([
            ...permissionService.normalizeAllowedActions(client.allowed_actions),
            ...ownerFallbackAllowedActions,
          ]),
        )
        const isActiveClient = client.is_active || String(client.id) === String(user?.user?.active_client)

        return {
        ...client,
        id:client.id,
        long_name:client.long_name,
        navigate:`${client.id}`,
        roles:roleNames.length > 0 ? roleNames : '',
        invitation:false,
        allowed_actions:allowedActions,
        dropOptions: isActiveClient ? clientActions : [selectClientAction],
        date_created:timeConverterMMDDYYHHMM(client.date_created),
        is_active_badge: isActiveClient ?
          <span style={{
            padding:'2px 8px',
            borderRadius:'4px',
            fontSize:'12px',
            fontWeight:500,
            backgroundColor:'#17C964',
            color:'#fff',
          }}>
            {t("ACTIVE")}
          </span> : null,
      }})
      setPaginationData(res.data)
      setRows(data)
      setPageLoading(false)
    })
    .catch((e) => {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
      setPageLoading(false)
    })
  }
  const {viewAlert} = useUi()
  const navigate = useNavigate()
  const canDissociate = (id:any) => {
    clientService.canDissociate(id)
    .then((res) => {
      if(res.data.can_dissociate === false){
        viewAlert({
          severity:'info',
          message:`${t("CAN'T_DISSOCIATE_CLIENT")}`
        })
      } else {
       
        clientService.dissociateFromClient(id)
        .then((res) => {
          if(res.data.detail){
            viewAlert({
              severity:'info',
              message:res.data.detail
            })
          }
          updateUser()
        })
        .catch((e) => {
          if(e?.response?.data?.detail){
            viewAlert({
              severity:'error',
              message:e?.response?.data?.detail
            })
          }
        })
      }
    })
    .catch((e) => {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    })
  }

  const setActiveClient = (item:any) => {
    clientService.setActive(item.id)
    .then(() => {
      authService.getUser()
      .then((res:any) => {
        setUser(res.data)
        updateList()
        viewAlert({
          severity:'success',
          message:`${t("CLIENT_SELECTED")} ${item.long_name} #${item.id}`
        })
      })
    })
    .catch((e:any) => {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    })
  }

  useEffect(() => {
    updateList()
  },[page, ordering, pageSize, search])

  useEffect(() => {
    const interval = setInterval(() => {
      updateList(true)
    }, 10000)
    return () => clearInterval(interval)
  },[ordering, page, pageSize, search])
  const openInviteModal = async (item:any) => {
    const roles:any = await clientService.getAvailableRoles(item.id)
    const notifications:any = await clientService.getUserNotificationSettings(item.id)
    setAvailableNotifications(
      Object.entries(notifications.data.detail).map((e) => (e[1]))
    )
    setAvailableRoles(roles.data.roles)
    setClientToInvite(item)
    setModalType('invite-user')
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModal = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const selectClientAction = {
    title:t("SELECT_CLIENT"),
    id:'1',
    icon:icons.CheckMark,
    onClick: (item:any) => setActiveClient(item),
    divider:false
  }
  const clientActions = [
    {
      title:t("INVITE_USER"),
      id:'5',
      icon:icons.Clients,
      permissionSurface: permissionSurfaces.rowActions.clientInvite,
      onClick: (item:any) => openInviteModal(item),
      divider:false
    },
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      permissionSurface: permissionSurfaces.rowActions.clientEdit,
      onClick: (item:any) => navigate(`${item.id}/client-edit`),
      divider:false
    },
    {
      title:t("DISSOCIATE"),
      id:'4',
      permissionSurface: permissionSurfaces.rowActions.clientDissociate,
      onClick:(item:any) => canDissociate(item.id),
      icon:icons.MinusDefault,
      divider:false
    },
  ]

  const columns = [
    {
      title:t("ID"),
      key:"id",
      sorted:true
    },
    {
      title:t("NAME"),
      key:'long_name',
      sorted:true
    },
    {
      title:t("YOUR_ROLES"),
      key:"roles",
      withBackground:true,
    },
    {
      title:t("CREATED_AT"),
      key:"date_created",
      sorted:true
    },
    {
      title:t("STATUS"),
      key:"is_active_badge"
    },
    {
      title:"",
      key:'props'
    }
  ]
  return(
    <Container>
      <TopProgress loading={pageLoading}/>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <InstanceName>
          {t("CLIENTS")}
        </InstanceName>
        <Button
          variant="primary"
          size="small"
          title={t("CREATE_CLIENT")}
          onClick={() => navigate('/complete-registration')}
        />
      </div>
      {pageLoading ?
        <TableSkeleton
            rows={1}
            columns={columns?.length || 5}
            columnsData={columns}
          />
      :
        <Table
          columns={columns}
          rows={rows}
          hideCheckbox
          paginationData={paginationData}
          updateData={updateList}
          loading={pageLoading}
          searchable
        />
      }
      <ModalVariables
        modalType={modalType}
        closeModalCancel={closeModal}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
      />
    </Container>
  )
}

export default Clients
